import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Search, User, X } from 'lucide-react'

import { useSearchUsers } from '@/application/services/user.service'
import { Skeleton } from '@/ui/components/common/Skeleton'

export interface AuthorState {
  id: string | null
  name: string
  /** Login of the selected user, for domains keyed by username rather than id. */
  username: string | null
}

interface AuthorSelectorProps {
  value: AuthorState
  onChange: (author: AuthorState) => void
  /** Set to false when the domain requires an author (leads). */
  allowClear?: boolean
}

/**
 * Combobox-style author picker: renders the currently-selected author as a
 * removable badge plus a debounced user-search input with a results dropdown
 * that closes on outside-click.
 */
export function AuthorSelector({ value, onChange, allowClear = true }: AuthorSelectorProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data: results, isLoading } = useSearchUsers(query)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleSelect = (user: {
    userId: string
    username: string | null
    displayName: string | null
  }) => {
    onChange({
      id: user.userId,
      name: user.displayName ?? user.username ?? user.userId,
      username: user.username,
    })
    setQuery('')
    setOpen(false)
  }

  const handleClear = () => {
    onChange({ id: null, name: '', username: null })
    setQuery('')
  }

  return (
    <div className="space-y-2">
      {/* Current author badge */}
      {value.name ? (
        <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg text-sm">
          <User className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium text-foreground flex-1">{value.name}</span>
          {allowClear && (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 rounded hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
              title={t('admin.form_author_clear')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic px-1">{t('admin.form_author_none')}</p>
      )}

      {/* Search input + dropdown */}
      <div ref={containerRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setOpen(true)
            }}
            onFocus={() => query.length >= 2 && setOpen(true)}
            placeholder={t('admin.form_author_search_placeholder')}
            className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-colors"
          />
        </div>

        {/* Results dropdown */}
        {open && query.length >= 2 && (
          <div className="absolute top-full mt-1 left-0 right-0 bg-popover border border-border rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
            {isLoading ? (
              <div className="px-3 py-2 space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ) : !results || results.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground">{t('common.error')}</p>
            ) : (
              results.map(
                (user: {
                  userId: string
                  username: string | null
                  displayName: string | null
                  role: string
                }) => (
                  <button
                    key={user.userId}
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault()
                      handleSelect(user)
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-accent hover:text-accent-foreground text-left transition-colors"
                  >
                    <div className="h-7 w-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary shrink-0">
                      {(user.displayName ?? user.username ?? '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {user.displayName ?? user.username}
                      </p>
                      {user.displayName && (
                        <p className="text-xs text-muted-foreground truncate">@{user.username}</p>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground capitalize shrink-0">
                      {user.role}
                    </span>
                  </button>
                ),
              )
            )}
          </div>
        )}
      </div>
    </div>
  )
}
